import { useId } from 'react';
import type { PnidWizardState } from '../types';
import WorkspaceTargetRail from './WorkspaceTargetRail';
import { equipmentTargets, instrumentTargetGroups } from './targetRailItems';

export default function EquipmentInstrumentRail({
  tab, onTabChange, assets, activeEquipmentId, selectedInstrumentId,
  onSelectEquipment, onSelectInstrument, onDrawNew, drawNewActive, stale, loading, error,
  onRefresh, refreshDisabled, historicalEquipment, onExitPreview,
}: {
  tab: 'equipment' | 'instruments';
  onTabChange: (tab: 'equipment' | 'instruments') => void;
  assets?: Pick<PnidWizardState, 'equipments' | 'instruments'>;
  activeEquipmentId: string | null;
  selectedInstrumentId: string | null;
  onSelectEquipment: (id: string) => void;
  onSelectInstrument: (id: string) => void;
  onDrawNew: () => void;
  drawNewActive: boolean;
  stale: boolean;
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
  refreshDisabled?: boolean;
  historicalEquipment?: PnidWizardState['equipments'];
  onExitPreview: () => void;
}) {
  const tabId = useId();
  const historical = historicalEquipment !== undefined;
  const historicalInstruments = historical && tab === 'instruments';
  const unavailable = historicalInstruments || (!historical && (stale || loading || !!error));
  const groups = tab === 'instruments' ? instrumentTargetGroups(assets?.instruments ?? []) : undefined;
  const refreshButton = (
    <button type="button" className="pnid-target-sort" onClick={onRefresh}
      disabled={loading || refreshDisabled}>
      {loading ? 'Refreshing…' : error ? 'Retry' : 'Refresh list'}
    </button>
  );
  return (
    <WorkspaceTargetRail key={tab}
      title={tab === 'equipment' ? 'Equipment' : 'Instruments'}
      panelId={`${tabId}-panel`} labelledBy={`${tabId}-${tab}`}
      keepEmpty groups={groups}
      items={groups ? groups.flatMap((group) => group.items) : equipmentTargets(historicalEquipment ?? assets?.equipments ?? [])}
      activeId={tab === 'equipment' ? activeEquipmentId : selectedInstrumentId}
      onSelect={tab === 'equipment' ? onSelectEquipment : onSelectInstrument}
      onDrawNew={onDrawNew} drawNewLabel={tab === 'instruments' ? 'Draw New Instrument' : 'Draw New Equipment'}
      drawNewActive={drawNewActive}
      unavailable={unavailable}
      header={<>
        <div className="pnid-target-tabs" role="tablist" aria-label="Review assets">
          {(['equipment', 'instruments'] as const).map((value) => (
            <button key={value} id={`${tabId}-${value}`} type="button" role="tab"
              aria-selected={tab === value} aria-controls={`${tabId}-panel`} tabIndex={tab === value ? 0 : -1}
              onClick={() => onTabChange(value)}
              onKeyDown={(event) => {
                if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
                event.preventDefault();
                event.stopPropagation();
                const next = event.key === 'Home' ? 'equipment' : event.key === 'End' ? 'instruments'
                  : value === 'equipment' ? 'instruments' : 'equipment';
                onTabChange(next);
                requestAnimationFrame(() => document.getElementById(`${tabId}-${next}`)?.focus());
              }}>
              {value === 'equipment' ? 'Equipment' : 'Instruments'}
            </button>
          ))}
        </div>
        {!historical && !unavailable && <div className="pnid-target-refresh">{refreshButton}</div>}
      </>}
      overlay={unavailable ? <>
        <p>{historicalInstruments ? 'Return to the current drawing to review instruments.'
          : loading ? 'Loading saved assets…'
          : error || 'Drawing changed. Refresh the list to review saved changes.'}</p>
        {historicalInstruments
          ? <button type="button" className="pnid-target-sort" onClick={onExitPreview}>Exit Preview</button>
          : refreshButton}
      </> : undefined}
    />
  );
}
